/* ============================================================
   DW Arcade — Darts (skill game). A TRUE 3D game (the factory carries
   `.is3D = true`): a real Three.js dart leaves the hand, ARCS through the air
   under gravity, and thunks into the modeled dartboard where the physics put it.

   CONTROL MODEL — "AIM, THEN FLICK" (rebuilt 2026-06-16). Aiming and throwing
   are TWO fully decoupled steps, so the act of throwing can never drag your
   point of aim off-target (the old fused gesture is gone):

     1) AIM  — tap / drag on the BOARD to move a steady reticle (mouse: hover
               also aims; keyboard/D-pad: 4-way nudge). Lifting does NOT throw —
               your aim stays put, so you can line it up unobstructed.
     2) FLICK— a dedicated FLICK LANE is docked at the bottom of the screen.
               Flick up in the lane to throw. The flick sets POWER only (from how
               far you flick); it can NEVER change your aim. A generous sweet
               band lands the dart on the pip; a short flick drops low, a long
               flick sails high — deterministic and learnable, NEVER random.
               Space / Enter run a timing-bar fallback for keyboard players.

   9 darts, standard board scoring (singles · doubles ×2 · triples ×3 · 25 /
   bull 50). Score → reward tier (TIER_TABLES.darts in dw-arcade.js).

   3D GAME-MODULE CONTRACT (engine = dw-arcade-2d.js → buildDarts):
     window.DwArcadeGames.darts(opts) -> {
       is3D, controls,
       attach(ctx), detach(),          // ctx = { THREE, scene, machine, camera, renderer, mount, dev, viewPos }
       start(), stop(), reset(),
       update(dtSeconds),              // step flight + animate; returns running flag
       pointerDown(e), pointerMove(e), pointerUp(e),   // BOARD aim only
       keyAim(dir), setDir(dir), keyThrow(), press(btn),// keyboard / on-screen fallback
       drawAttract(name), showTuner(on),
       getScore(), getLives(), getState()
     }
   opts: { dev, onScore(score), onState(state), onEnd({score, balls, won}) }

   Board-space (local to the rig group): X across the face (+right), Y up the
   face (+up), Z out of the face toward the player. The rig is seated on the
   modeled "Dartboard" box and fine-tuned with the DARTS DEBUG panel. The dart
   flies in WORLD space (world gravity) so a tilted board still behaves right.
   ============================================================ */
(function () {
  'use strict';

  // Palette — canvas/JS can't read CSS vars, so mirror the brand tokens here
  // (the arcade is the intentional "fun zone" departure; this file is the
  // single source for its colours, same pattern as the other game modules).
  var C = {
    cream: '#E9DCC3', black: '#181a1f', red: '#D52B3A', green: '#1A9E3E',
    redHex: 0xD52B3A, greenHex: 0x1A9E3E, gold: 0xD4A437, goldCss: '#D4A437',
    wire: '#9aa0ab', surround: '#0d0a16', num: '#F7F8FA',
    dartBarrel: 0xC8CCD4, dartShaft: 0x2D1B4E, flightA: 0x22B84A, flightB: 0xD4A437,
    reticle: 0xF7F8FA
  };

  // Standard dartboard, numbers clockwise from the top (20 at 12 o'clock).
  var NUMBERS = [20, 1, 18, 4, 13, 6, 10, 15, 2, 17, 3, 19, 7, 16, 8, 11, 14, 9, 12, 5];
  // Ring radii as fractions of the scoring radius (double-ring outer edge = 1).
  // Real board (mm / 170): bull50 6.35, bull25 15.9, triple 99–107, double 162–170.
  var F = { b50: 0.0374, b25: 0.0935, tIn: 0.5824, tOut: 0.6294, dIn: 0.9529, dOut: 1.0 };
  var DARTS_PER_GAME = 9;

  // Default rig tune (fractions of the dartboard box unless noted). Tuned live
  // in the DARTS DEBUG panel, then pasted back into DARTS_TUNE.
  var TUNE_DEFAULT = {
    ox: 0, oy: 0, oz: 0,          // board-centre nudge (m) along R / U / N
    roll: 0,                      // rotate the number wheel about N (deg) — align "20" to top
    yaw: 0, pitch: 0,             // fine-tune the board normal (deg)
    flip: 0,                      // 1 = flip the normal if it points away from the player
    radiusF: 0.82,               // scoring radius as fraction of the modeled disc radius
    faceProud: 0.012,            // our face sits this far proud of the model (m) along N
    showFace: 1,                 // 1 = draw our own WYSIWYG dartboard face
    dartLenF: 0.4,               // dart length as fraction of scoring radius
    handDistF: 0.32,             // hand distance from the eye toward the board (× eye→board distance)
    handDownF: 0.2,              // hold the dart this far below the line of sight (× hand distance)
    handSideF: 0.12,             // …and this far to the (right) side (× hand distance)
    grav: 2.4,                   // world gravity for the flight (scene units / s^2) — sets the arc bow
    flightT: 0.38,               // reference flight time (s) for the visible arc
    // --- power / accuracy (forgiving by design) ---
    powerSweet: 0.62,            // power that lands dead on the pip
    powerBand: 0.18,             // half-width of the "on target" zone — GENEROUS so a normal flick is true
    powerKick: 0.55,             // vertical miss at full power error (fraction of radius): too hard = high, too soft = low
    sideKick: 0,                 // lateral miss at full power error — 0 = aim alone owns left/right (no random-feeling scatter).
                                 //   Raise it in DARTS DEBUG to add horizontal challenge; it's deterministic, never random.
    flickFullF: 0.55,            // upward flick distance for FULL power, as a fraction of the canvas height.
                                 //   sweet (~0.62) lands at ~0.34 of the screen — a comfortable, controlled flick.
    barPeriod: 1.4               // seconds for one full 0→1→0 sweep of the keyboard timing bar (slower = easier)
  };
  // Tuned in-scene (DARTS DEBUG → Copy JSON), pasted here.
  var DARTS_TUNE = {};

  function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }
  function now() { return performance.now() / 1000; }

  function create(opts) {
    opts = opts || {};
    var dev = !!opts.dev;
    var onEnd = opts.onEnd || function () {};
    var onScore = opts.onScore || function () {};
    var onState = opts.onState || function () {};

    var T = null, scene = null, machine = null, camera = null, renderer = null, mount = null, viewPos = null;
    var rig = null, faceMesh = null, reticle = null, hitMark = null;
    var darts = [];                 // every dart mesh built this game (held + stuck)
    var held = null;                // the dart currently in hand
    var plane = null;               // THREE.Plane of the board face (world)
    var tune = Object.assign({}, TUNE_DEFAULT, DARTS_TUNE);
    var geom = null;

    var coach = null, powerEl = null, popHost = null, fbEl = null, lane = null;   // DOM overlays
    var laneHandle = null, laneHint = null, lockBtn = null, reaimBtn = null;

    // 'aiming' → 'locked' → 'throwing' is the explicit TWO-STAGE flow: you AIM, then
    // LOCK the aim (tap LOCK / press Space), and only THEN can you throw (flick / Space).
    var state = 'attract';          // 'attract' | 'aiming' | 'locked' | 'throwing' | 'between' | 'over'
    var score = 0, dartsLeft = DARTS_PER_GAME, lastPowerErr = 0;
    var aim = { x: 0, y: 0 };       // intended landing in board-space (metres)
    var active = null;              // flying dart body { mesh, pv, vv, pp, pd, t, G }
    var aimPointerId = null;        // the pointer currently dragging the BOARD to aim
    var flick = null;               // throw gesture in the lane: { id, y0, power }
    var charge = { on: false, t: 0, val: 0, since: 0 };   // keyboard timing-bar fallback
    var demoMode = false, demoAt = 0;   // free self-playing attract (engine demo-first flow): AI flag + next auto-throw time

    // Coach with the right verb per device: a finger "flicks", a mouse "drags".
    var coarse = false;
    try { coarse = (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) || ('ontouchstart' in window); } catch (e) { coarse = ('ontouchstart' in window); }
    function isTouchEvt(e) { return !!(e && (e.pointerType === 'touch' || e.pointerType === 'pen')) || coarse; }

    function setState(s) {
      if (state === s) return;
      state = s; onState(s);
      updateControlUI();
      if (s === 'aiming' && demoMode) scheduleDemo();   // queue the next hands-off throw
    }
    // Show the right control affordance for the current stage (touch only): LOCK
    // while AIMING, the flick lane + Re-aim while LOCKED. Desktop drives it all from
    // the keyboard, so its lane/buttons stay hidden (set in buildLane / buildButtons).
    function updateControlUI() {
      var laneShow = (state === 'locked') && !demoMode && coarse;
      if (lane) {
        lane.style.opacity = laneShow ? '1' : '0';
        lane.style.pointerEvents = laneShow ? 'auto' : 'none';
      }
      if (lockBtn) lockBtn.style.display = (state === 'aiming' && !demoMode && coarse) ? '' : 'none';
      if (reaimBtn) reaimBtn.style.display = (state === 'locked' && !demoMode && coarse) ? '' : 'none';
    }

    // ---- Build from the modeled board's world box ----
    function attach(ctx) {
      T = ctx.THREE; scene = ctx.scene; machine = ctx.machine;
      camera = ctx.camera; renderer = ctx.renderer; mount = ctx.mount;
      viewPos = ctx.viewPos || null;
      buildGeom();
      buildMeshes();
      buildOverlays();
      newHeldDart();
      drawAttract(machine && machine.name);
    }

    function buildGeom() {
      var box = machine.box;                         // THREE.Box3, world
      var size = box.getSize(new T.Vector3());
      var center = box.getCenter(new T.Vector3());

      // Thinnest box axis = the board's facing normal; the other two span the disc.
      var dims = [['x', size.x], ['y', size.y], ['z', size.z]].sort(function (a, b) { return a[1] - b[1]; });
      var thinAxis = dims[0][0], thick = dims[0][1];
      var faceDims = [dims[1][1], dims[2][1]];
      var discR = Math.max(faceDims[0], faceDims[1]) / 2;   // modeled disc radius (incl. surround)

      var N = new T.Vector3(); N[thinAxis] = 1;
      // Orient the normal toward the player (the step-up view), then fine-tune.
      var eye = viewPos ? viewPos.clone() : (camera ? camera.position.clone() : center.clone().add(new T.Vector3(0, 0, 1)));
      if (N.dot(eye.clone().sub(center)) < 0) N.negate();
      if (tune.flip) N.negate();
      var up = new T.Vector3(0, 1, 0);
      if (Math.abs(N.dot(up)) > 0.95) up.set(0, 0, 1);       // board lying flat — pick a sane up
      if (tune.yaw) N.applyAxisAngle(up, T.MathUtils.degToRad(tune.yaw));
      var R0 = new T.Vector3().crossVectors(up, N).normalize();
      if (tune.pitch) N.applyAxisAngle(R0, T.MathUtils.degToRad(tune.pitch));
      N.normalize();
      var R = new T.Vector3().crossVectors(up, N).normalize();
      var U = new T.Vector3().crossVectors(N, R).normalize();

      var Rb = discR * tune.radiusF;                  // scoring radius (double-out)
      // Board-face centre: box centre pushed to the front face, plus nudges.
      var Cw = center.clone()
        .addScaledVector(N, thick / 2)
        .addScaledVector(R, tune.ox).addScaledVector(U, tune.oy).addScaledVector(N, tune.oz);

      var measured = Math.max(viewPos ? Math.abs(eye.clone().sub(Cw).dot(N)) : Rb * 7, Rb * 3);
      var faceCenter = Cw.clone().addScaledVector(N, tune.faceProud);   // the visible face plane
      var handDist = measured * tune.handDistF;                          // eye → hand (arm's length)

      geom = {
        Cw: Cw, N: N, R: R, U: U, Rb: Rb, discR: discR, thick: thick,
        roll: T.MathUtils.degToRad(tune.roll),
        faceProud: tune.faceProud, faceCenter: faceCenter,
        dartLen: Rb * tune.dartLenF,
        eye: eye.clone(), measured: measured,
        handDist: handDist, handDown: handDist * tune.handDownF, handSide: handDist * tune.handSideF,
        grav: tune.grav, flightT: tune.flightT,
        powerSweet: tune.powerSweet, powerBand: tune.powerBand, powerKick: tune.powerKick, sideKick: tune.sideKick,
        flickFullF: clamp(tune.flickFullF, 0.2, 1.2), barPeriod: Math.max(tune.barPeriod, 0.3),
        aimLimit: Rb * 1.18,
        floorY: Cw.y - Rb * 2.6
      };
      plane = new T.Plane().setFromNormalAndCoplanarPoint(N, faceCenter);
    }

    // How "loaded" the throw is right now (0..1) — drives the held dart's wind-up.
    // A flick in the lane and the keyboard timing-bar share this.
    function loadAmt() { return flick ? flick.power : (charge.on ? charge.val : 0); }

    // The hand pose for the current aim: the dart sits an arm's length down the
    // line of sight to the target, held a touch below & to the side so you sight
    // OVER it. Loading the throw cocks it back toward the player.
    function handPose() {
      var Tw = toWorld(aim.x, aim.y, geom.faceProud);
      var sight = Tw.clone().sub(geom.eye); var dist = sight.length() || 1; sight.multiplyScalar(1 / dist);
      var vRight = new T.Vector3().crossVectors(sight, new T.Vector3(0, 1, 0));
      if (vRight.lengthSq() < 1e-6) vRight.set(1, 0, 0); else vRight.normalize();
      var vUp = new T.Vector3().crossVectors(vRight, sight).normalize();
      var windup = loadAmt() * geom.handDist * 0.18;     // cock back as power builds
      var pos = geom.eye.clone()
        .addScaledVector(sight, geom.handDist - windup)
        .addScaledVector(vRight, geom.handSide)
        .addScaledVector(vUp, -geom.handDown);
      return { pos: pos, dir: Tw.clone().sub(pos).normalize() };
    }

    // board-space (x=R, y=U, z=N) -> world
    function toWorld(x, y, z) { return geom.Cw.clone().addScaledVector(geom.R, x).addScaledVector(geom.U, y).addScaledVector(geom.N, z); }
    // world -> board-space
    function toBoard(w) { var d = w.clone().sub(geom.Cw); return { x: d.dot(geom.R), y: d.dot(geom.U), z: d.dot(geom.N) }; }

    function setRigTransform() {
      var m = new T.Matrix4().makeBasis(geom.R, geom.U, geom.N);
      rig.quaternion.setFromRotationMatrix(m);
      rig.position.copy(geom.Cw);
    }

    function buildMeshes() {
      disposeRig();
      rig = new T.Group(); setRigTransform(); scene.add(rig);

      if (tune.showFace) {
        var tex = new T.CanvasTexture(makeFaceCanvas());
        tex.colorSpace = T.SRGBColorSpace; tex.anisotropy = 4;
        var discMeshR = geom.Rb * 1.25;               // canvas scoring edge sits at 0.8 of the disc
        faceMesh = new T.Mesh(
          new T.CircleGeometry(discMeshR, 64),
          new T.MeshStandardMaterial({ map: tex, roughness: 0.6, metalness: 0.05 })
        );
        faceMesh.position.set(0, 0, geom.faceProud);
        faceMesh.rotation.z = geom.roll;              // align "20" to top against the model
        rig.add(faceMesh);
      }

      // Aim reticle — a thin ring + crosshair on the board face.
      var rg = new T.Group();
      var rR = geom.Rb * 0.11;
      var ring = new T.Mesh(new T.TorusGeometry(rR, Math.max(geom.Rb * 0.012, 0.002), 8, 28),
        new T.MeshBasicMaterial({ color: C.reticle, transparent: true, opacity: 0.9 }));
      rg.add(ring);
      [[1, 0], [0, 1]].forEach(function (a) {
        var bar = new T.Mesh(new T.BoxGeometry(a[0] ? rR * 1.7 : geom.Rb * 0.02, a[1] ? rR * 1.7 : geom.Rb * 0.02, geom.Rb * 0.02),
          new T.MeshBasicMaterial({ color: C.reticle, transparent: true, opacity: 0.7 }));
        rg.add(bar);
      });
      reticle = rg; reticle.position.set(0, 0, geom.faceProud + geom.Rb * 0.02); rig.add(reticle);

      // Hit marker — a quick expanding ring shown where a dart lands.
      hitMark = new T.Mesh(new T.RingGeometry(geom.Rb * 0.06, geom.Rb * 0.1, 24),
        new T.MeshBasicMaterial({ color: C.gold, transparent: true, opacity: 0, side: T.DoubleSide }));
      hitMark.position.z = geom.faceProud + geom.Rb * 0.03; rig.add(hitMark);

      updateReticle();
    }

    // ---- The dartboard face, painted to a canvas (so what you see = what you score) ----
    function makeFaceCanvas() {
      var c = document.createElement('canvas'); c.width = c.height = 1024;
      var ctx = c.getContext('2d'), s = c.width, cx = s / 2, cy = s / 2;
      var boardR = s * 0.40;                          // scoring (double-out) radius in px
      var rad = function (f) { return boardR * f; };
      ctx.clearRect(0, 0, s, s);

      // Dark surround behind the wheel + the number band.
      ctx.fillStyle = C.surround; ctx.beginPath(); ctx.arc(cx, cy, boardR * 1.24, 0, 7); ctx.fill();

      // Draw each wedge by rotating the context so the wedge sits at the top.
      var seg = Math.PI / 10;                          // 18°
      function wedgeSector(r0, r1, halfSpan, color) {
        ctx.beginPath();
        ctx.arc(cx, cy, r1, -Math.PI / 2 - halfSpan, -Math.PI / 2 + halfSpan);
        ctx.arc(cx, cy, r0, -Math.PI / 2 + halfSpan, -Math.PI / 2 - halfSpan, true);
        ctx.closePath(); ctx.fillStyle = color; ctx.fill();
      }
      for (var k = 0; k < 20; k++) {
        ctx.save(); ctx.translate(cx, cy); ctx.rotate(k * seg); ctx.translate(-cx, -cy);
        var dark = (k % 2 === 0);                       // 20 (k=0) is black
        var body = dark ? C.black : C.cream;
        var band = dark ? C.red : C.green;
        // single (inner), triple band, single (outer), double band
        wedgeSector(rad(F.b25), rad(F.tIn), seg / 2, body);
        wedgeSector(rad(F.tIn), rad(F.tOut), seg / 2, band);
        wedgeSector(rad(F.tOut), rad(F.dIn), seg / 2, body);
        wedgeSector(rad(F.dIn), rad(F.dOut), seg / 2, band);
        ctx.restore();
      }
      // Bull: 25 (green) + 50 (red).
      ctx.beginPath(); ctx.arc(cx, cy, rad(F.b25), 0, 7); ctx.fillStyle = C.green; ctx.fill();
      ctx.beginPath(); ctx.arc(cx, cy, rad(F.b50), 0, 7); ctx.fillStyle = C.red; ctx.fill();

      // The "spider" — thin wires between segments + ring circles.
      ctx.strokeStyle = C.wire; ctx.lineWidth = Math.max(boardR * 0.006, 1.5);
      [F.b25, F.tIn, F.tOut, F.dIn, F.dOut].forEach(function (f) { ctx.beginPath(); ctx.arc(cx, cy, rad(f), 0, 7); ctx.stroke(); });
      for (var w = 0; w < 20; w++) {
        var a = w * seg - Math.PI / 2 + seg / 2;       // boundary lines sit between wedges
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(a) * rad(F.b25), cy + Math.sin(a) * rad(F.b25));
        ctx.lineTo(cx + Math.cos(a) * rad(F.dOut), cy + Math.sin(a) * rad(F.dOut));
        ctx.stroke();
      }

      // Numbers around the outside.
      ctx.fillStyle = C.num; ctx.font = '700 ' + Math.round(boardR * 0.12) + 'px Arial, sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      for (var i = 0; i < 20; i++) {
        var ang = i * seg - Math.PI / 2;               // wedge centre, from top, clockwise
        var nr = boardR * 1.13;
        ctx.fillText(String(NUMBERS[i]), cx + Math.cos(ang) * nr, cy + Math.sin(ang) * nr);
      }
      return c;
    }

    // ---- Scoring: board-space point -> { v, label, ring } ----
    function scoreAt(x, y) {
      var r = Math.hypot(x, y) / geom.Rb;
      if (r > F.dOut) return { v: 0, label: 'MISS', ring: 'miss' };
      if (r <= F.b50) return { v: 50, label: 'BULLSEYE', ring: 'bull' };
      if (r <= F.b25) return { v: 25, label: '25', ring: 'outer-bull' };
      // 0 at top, +toward right (clockwise); + roll so scoring tracks a board the
      // tuner has rotated to sit "20-up" against the model.
      var ang = Math.atan2(x, y) + geom.roll; ang = ((ang % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
      var idx = Math.round(ang / (Math.PI / 10)) % 20;
      var n = NUMBERS[idx];
      if (r >= F.tIn && r <= F.tOut) return { v: n * 3, label: 'TRIPLE ' + n, ring: 'triple' };
      if (r >= F.dIn && r <= F.dOut) return { v: n * 2, label: 'DOUBLE ' + n, ring: 'double' };
      return { v: n, label: String(n), ring: 'single' };
    }

    // ---- Dart mesh (points along +Z = tip). Real-dart silhouette: steel point,
    // tungsten barrel, thin shaft, and FOUR fins that radiate from the shaft
    // (each fin lives outboard of the axis, so they never intersect each other). ----
    function makeDart() {
      var g = new T.Group();
      var L = geom.dartLen, r = L * 0.05;
      var matTip = new T.MeshStandardMaterial({ color: 0x6B7280, roughness: 0.35, metalness: 0.9 });
      var matBarrel = new T.MeshStandardMaterial({ color: C.dartBarrel, roughness: 0.28, metalness: 0.92 });
      var matShaft = new T.MeshStandardMaterial({ color: C.dartShaft, roughness: 0.5, metalness: 0.3 });
      var matFA = new T.MeshStandardMaterial({ color: C.flightA, roughness: 0.45, metalness: 0, side: T.DoubleSide });
      var matFB = new T.MeshStandardMaterial({ color: C.flightB, roughness: 0.45, metalness: 0, side: T.DoubleSide });
      function tube(len, r0, r1, mat, z) {
        var m = new T.Mesh(new T.CylinderGeometry(r0, r1, len, 16), mat);
        m.rotation.x = Math.PI / 2; m.position.z = z; return m;        // +Y cylinder -> +Z
      }
      // steel point
      var tip = new T.Mesh(new T.ConeGeometry(r * 0.55, L * 0.2, 14), matTip);
      tip.rotation.x = -Math.PI / 2; tip.position.z = L * 0.42; g.add(tip);
      g.add(tube(L * 0.06, r * 0.55, r * 0.95, matBarrel, L * 0.3));    // tip→barrel shoulder
      g.add(tube(L * 0.34, r * 0.95, r * 0.85, matBarrel, L * 0.1));    // barrel
      g.add(tube(L * 0.06, r * 0.85, r * 0.34, matShaft, L * -0.1));    // barrel→shaft
      g.add(tube(L * 0.24, r * 0.34, r * 0.34, matShaft, L * -0.26));   // shaft
      // four fins radiating from the shaft surface (inner edge at the shaft, so
      // opposite fins are split by the shaft and adjacent fins meet only at it).
      var innerR = r * 0.34, outR = r * 3.1, finLen = L * 0.2, finZ = -L * 0.36;
      for (var i = 0; i < 4; i++) {
        var pg = new T.PlaneGeometry(outR - innerR, finLen);           // X = radial, Y = along axis
        pg.translate(innerR + (outR - innerR) / 2, 0, 0);
        pg.rotateX(-Math.PI / 2);                                       // lay Y(length) along Z
        var fin = new T.Mesh(pg, (i % 2) ? matFB : matFA);
        fin.rotation.z = i * Math.PI / 2; fin.position.z = finZ; g.add(fin);
      }
      g.userData.len = L;
      scene.add(g); darts.push(g);
      return g;
    }

    function newHeldDart() {
      held = makeDart();
      held.userData.stuck = false;
      poseHeldDart(0);
    }
    // Park the held dart at the (aim-tracking) hand pose, pointed at the target.
    function poseHeldDart(bob) {
      if (!held) return;
      var hp = handPose();
      if (bob) hp.pos.addScaledVector(geom.U, bob);
      held.position.copy(hp.pos);
      orientDart(held, hp.dir);
    }
    function orientDart(d, dir) {
      d.quaternion.setFromUnitVectors(new T.Vector3(0, 0, 1), dir.clone().normalize());
    }

    function clearDarts() {
      darts.forEach(function (d) {
        d.traverse(function (o) { if (o.geometry) o.geometry.dispose(); if (o.material) { (Array.isArray(o.material) ? o.material : [o.material]).forEach(function (m) { m.dispose(); }); } });
        if (d.parent) d.parent.remove(d);
      });
      darts = []; held = null; active = null;
    }

    // ---- Overlays (coach line, power bar w/ sweet zone, score pops, feedback, flick lane) ----
    function buildOverlays() {
      if (!mount) return;
      if (!coach) {
        coach = document.createElement('div');
        coach.style.cssText = 'position:absolute;left:50%;bottom:128px;transform:translateX(-50%);z-index:9;pointer-events:none;text-align:center;font-family:var(--font-body,sans-serif);color:#F7F8FA;text-shadow:0 2px 8px #000;font-weight:700;letter-spacing:.04em;text-transform:uppercase;font-size:13px;white-space:nowrap';
        mount.appendChild(coach);
      }
      if (!powerEl) {
        powerEl = document.createElement('div');
        powerEl.style.cssText = 'position:absolute;left:50%;bottom:104px;transform:translateX(-50%);z-index:9;pointer-events:none;width:min(280px,64vw);height:12px;background:rgba(8,5,19,.72);border:1px solid rgba(212,164,55,.6);border-radius:999px;overflow:hidden;opacity:0;transition:opacity .15s';
        var zone = document.createElement('i');   // sweet-spot band
        zone.style.cssText = 'position:absolute;top:0;bottom:0;background:rgba(34,184,74,.55);border-left:1px solid #22B84A;border-right:1px solid #22B84A';
        var fill = document.createElement('b');
        fill.style.cssText = 'position:absolute;top:0;bottom:0;left:0;width:0%;background:linear-gradient(90deg,#22B84A,#D4A437,#E63946);transition:width .04s linear;mix-blend-mode:screen';
        powerEl.appendChild(zone); powerEl.appendChild(fill); powerEl._fill = fill; powerEl._zone = zone;
        mount.appendChild(powerEl);
      }
      if (!fbEl) {
        fbEl = document.createElement('div');
        fbEl.style.cssText = 'position:absolute;left:50%;bottom:154px;transform:translateX(-50%);z-index:9;pointer-events:none;font-family:var(--font-display,sans-serif);font-weight:800;font-size:15px;letter-spacing:.06em;text-transform:uppercase;opacity:0;transition:opacity .2s';
        mount.appendChild(fbEl);
      }
      if (!popHost) {
        popHost = document.createElement('div');
        popHost.style.cssText = 'position:absolute;inset:0;z-index:9;pointer-events:none;overflow:hidden';
        mount.appendChild(popHost);
      }
      buildLane();
      buildButtons();
      positionSweetZone();
    }

    // LOCK (stage 1→2) and Re-aim (back to stage 1) — touch affordances. Desktop
    // does both from the keyboard, so they're hidden there.
    function buildButtons() {
      if (lockBtn || !mount) return;
      lockBtn = document.createElement('button');
      lockBtn.type = 'button';
      lockBtn.style.cssText = 'position:absolute;left:50%;bottom:110px;transform:translateX(-50%);z-index:11;display:none;' +
        'padding:13px 32px;background:linear-gradient(180deg,#22B84A,#1A9E3E);color:#F7F8FA;border:0;border-radius:4px;' +
        'font-family:var(--font-display,sans-serif);font-weight:800;font-size:16px;letter-spacing:.06em;text-transform:uppercase;' +
        'box-shadow:0 5px 0 #0c5a23,0 0 22px rgba(34,184,74,.5);cursor:pointer;touch-action:none';
      lockBtn.textContent = 'Lock Aim';
      lockBtn.addEventListener('click', function () { lockAim(false); });
      mount.appendChild(lockBtn);

      reaimBtn = document.createElement('button');
      reaimBtn.type = 'button';
      reaimBtn.style.cssText = 'position:absolute;left:10px;bottom:100px;z-index:11;display:none;' +
        'padding:8px 14px;background:rgba(8,5,19,.82);color:#F7F8FA;border:1px solid rgba(212,164,55,.5);border-radius:4px;' +
        'font-family:var(--font-body,sans-serif);font-weight:700;font-size:12px;letter-spacing:.05em;text-transform:uppercase;cursor:pointer';
      reaimBtn.textContent = '↺ Re-aim';
      reaimBtn.addEventListener('click', reaim);
      mount.appendChild(reaimBtn);
    }

    // The FLICK LANE — a bottom strip you flick up in to throw. It is the ONLY
    // throw surface for pointer input, so flicking can never disturb your aim.
    function buildLane() {
      if (lane) return;
      lane = document.createElement('div');
      lane.style.cssText = 'position:absolute;left:0;right:0;bottom:0;z-index:10;height:92px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;' +
        'background:linear-gradient(to top,rgba(8,5,19,.86),rgba(8,5,19,.18) 70%,rgba(8,5,19,0));' +
        'border-top:1px solid rgba(212,164,55,.32);touch-action:none;user-select:none;cursor:grab;opacity:0;transition:opacity .15s';
      if (!coarse) lane.style.display = 'none';        // desktop throws from the keyboard timing bar, not the lane
      // The dart handle: a chevron you flick up.
      laneHandle = document.createElement('div');
      laneHandle.style.cssText = 'font-family:var(--font-display,sans-serif);font-weight:800;font-size:26px;line-height:1;color:#D4A437;text-shadow:0 0 14px rgba(212,164,55,.7),0 2px 6px #000;transition:transform .05s linear;will-change:transform;pointer-events:none';
      laneHandle.textContent = '▲';
      laneHint = document.createElement('div');
      laneHint.style.cssText = 'font-family:var(--font-body,sans-serif);font-weight:700;font-size:12px;letter-spacing:.07em;text-transform:uppercase;color:#F7F8FA;text-shadow:0 1px 4px #000;pointer-events:none';
      laneHint.textContent = laneHintText();
      lane.appendChild(laneHandle); lane.appendChild(laneHint);
      lane.addEventListener('pointerdown', laneDown);
      lane.addEventListener('pointermove', laneMove);
      lane.addEventListener('pointerup', laneUp);
      lane.addEventListener('pointercancel', laneCancel);
      mount.appendChild(lane);
    }
    function laneHintText() { return coarse ? '↑ Flick up to throw' : '↑ Flick up to throw · or press Space'; }
    function liftHandle(px) {
      if (!laneHandle) return;
      var d = clamp(px, 0, 120);
      laneHandle.style.transform = 'translateY(' + (-d) + 'px) scale(' + (1 + d / 260) + ')';
    }

    function positionSweetZone() {
      if (!powerEl || !powerEl._zone || !geom) return;
      var lo = clamp(geom.powerSweet - geom.powerBand, 0, 1), hi = clamp(geom.powerSweet + geom.powerBand, 0, 1);
      powerEl._zone.style.left = (lo * 100) + '%'; powerEl._zone.style.width = ((hi - lo) * 100) + '%';
    }
    function setCoach(t) { if (coach) coach.textContent = t || ''; }
    function setPower(p) { if (powerEl) { powerEl.style.opacity = p > 0 ? '1' : '0'; powerEl._fill.style.width = Math.round(clamp(p, 0, 1) * 100) + '%'; } }
    function showFeedback(txt, color) {
      if (!fbEl) return;
      fbEl.textContent = txt; fbEl.style.color = color; fbEl.style.textShadow = '0 0 12px ' + color + ',0 2px 6px #000';
      fbEl.style.opacity = '1';
      clearTimeout(fbEl._t); fbEl._t = setTimeout(function () { fbEl.style.opacity = '0'; }, 800);
    }
    function scorePop(value, label, worldPos) {
      if (!popHost || !renderer || !camera) return;
      var v = worldPos.clone().project(camera);
      var rect = renderer.domElement.getBoundingClientRect();
      var x = (v.x * 0.5 + 0.5) * rect.width, y = (-v.y * 0.5 + 0.5) * rect.height;
      var color = value === 0 ? '#9aa0ab' : value >= 50 ? '#E63946' : value >= 40 ? '#D4A437' : value >= 20 ? '#22B84A' : '#9A7ADF';
      var el = document.createElement('div');
      el.innerHTML = (value > 0 ? '+' + value : 'MISS') + '<small style="display:block;font-size:.5em;letter-spacing:.08em;opacity:.92">' + label + '</small>';
      el.style.cssText = 'position:absolute;left:' + x + 'px;top:' + y + 'px;transform:translate(-50%,-50%);text-align:center;font-family:var(--font-display,sans-serif);font-weight:800;font-size:' + (value >= 50 ? 40 : 30) + 'px;color:' + color + ';text-shadow:0 0 14px ' + color + ',0 2px 6px #000;transition:all 1s cubic-bezier(.2,.8,.2,1);opacity:1';
      popHost.appendChild(el);
      requestAnimationFrame(function () { el.style.top = (y - 64) + 'px'; el.style.opacity = '0'; });
      setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 1050);
    }

    // ---- Lifecycle ----
    function aimCoach() { return coarse ? 'Drag the board to aim · then LOCK' : 'Aim with the mouse · SPACE to lock'; }
    function lockedCoach() { return coarse ? 'Flick up in the lane to throw' : 'Stop the bar in the green · SPACE'; }
    // Real scored run (called after a token is committed). Clears any demo and
    // forces a clean transition so the flick lane shows.
    function start() {
      demoMode = false; demoAt = 0;
      score = 0; dartsLeft = DARTS_PER_GAME; onScore(0);
      clearDarts(); cancelCharge(); flick = null; aimPointerId = null; aim.x = 0; aim.y = 0;
      newHeldDart(); updateReticle();
      state = 'attract';                 // force a real transition so overlays refresh
      setState('aiming');
      setCoach(aimCoach());
      setPower(0); resetLane();
      if (renderer) renderer.domElement.style.touchAction = 'none';
    }
    // Free, hands-off attract loop shown before a token is spent (engine demo-first
    // flow). An internal AI aims at believable targets and throws on a timer; it
    // NEVER ends or claims a reward — it just loops until start() takes over.
    function demo() {
      demoMode = true; demoAt = 0;
      score = 0; dartsLeft = DARTS_PER_GAME; onScore(0);
      clearDarts(); cancelCharge(); flick = null; aimPointerId = null; aim.x = 0; aim.y = 0;
      newHeldDart(); updateReticle();
      setCoach(''); setPower(0); resetLane();
      state = 'attract';
      setState('aiming');                // demoMode → setState queues the first auto-throw
    }
    function stop() {
      demoMode = false; demoAt = 0;
      setState('attract'); cancelCharge(); flick = null; aimPointerId = null;
      resetLane();
      if (renderer) renderer.domElement.style.touchAction = '';
    }
    function reset() {
      demoMode = false; demoAt = 0;
      score = 0; dartsLeft = DARTS_PER_GAME; clearDarts(); cancelCharge(); flick = null; aimPointerId = null;
      aim.x = 0; aim.y = 0; newHeldDart(); updateReticle();
      setState('attract'); setCoach(''); setPower(0); resetLane();
    }
    function resetLane() { if (laneHandle) liftHandle(0); if (laneHint) laneHint.textContent = laneHintText(); }

    // Believable demo targets (board-space): bull, treble-20, and a few scattered
    // pips so the attract loop looks like a real player working the board.
    function demoTargets() {
      var Rb = geom.Rb;
      return [
        { x: 0, y: 0 },                       // bull
        { x: 0, y: Rb * 0.606 },              // treble 20 (top)
        { x: Rb * 0.2, y: Rb * 0.22 },
        { x: -Rb * 0.34, y: -Rb * 0.16 },
        { x: Rb * 0.46, y: -Rb * 0.34 },
        { x: -Rb * 0.52, y: Rb * 0.3 }
      ];
    }
    function scheduleDemo() {
      if (!geom) return;
      var picks = demoTargets();
      var p = picks[Math.floor(Math.random() * picks.length)];
      setAim(p.x, p.y);
      demoAt = now() + 0.55 + Math.random() * 0.6;       // brief sight, then auto-throw
    }

    function nextDartOrEnd() {
      cancelCharge(); flick = null;
      if (dartsLeft <= 0) {
        if (demoMode) {                                  // attract loop never ends — recycle
          score = 0; dartsLeft = DARTS_PER_GAME; onScore(0);
          clearDarts(); newHeldDart();
          setState('aiming');                            // 'between' → 'aiming' re-queues a throw
          setPower(0);
          return;
        }
        setState('over');
        setCoach('That’s your 9 — final score ' + score);
        onEnd({ score: score, balls: DARTS_PER_GAME, won: false });   // worker decides the reward from score
        return;
      }
      newHeldDart();
      setState('aiming');
      if (!demoMode) setCoach('Dart ' + (DARTS_PER_GAME - dartsLeft + 1) + ' of ' + DARTS_PER_GAME);
      setPower(0); resetLane();
    }

    // ---- Aiming (BOARD only — never throws) ----
    function updateReticle() {
      if (reticle) reticle.position.set(aim.x, aim.y, geom.faceProud + geom.Rb * 0.02);
      if (held && state !== 'throwing') poseHeldDart(0);
    }
    function setAim(x, y) {
      var lim = geom.aimLimit;
      aim.x = clamp(x, -lim, lim); aim.y = clamp(y, -lim, lim);
      updateReticle();
    }
    // Raycast a screen point onto the board plane → board-space aim.
    function aimFromEvent(e) {
      if (!camera || !renderer || !plane) return false;
      var rect = renderer.domElement.getBoundingClientRect();
      var nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      var ny = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      var rc = new T.Raycaster(); rc.setFromCamera(new T.Vector2(nx, ny), camera);
      var hit = rc.ray.intersectPlane(plane, new T.Vector3());
      if (!hit) return false;
      var b = toBoard(hit); setAim(b.x, b.y); return true;
    }

    // Pointer handlers the engine wires to the CANVAS (board). These only AIM;
    // the lane (below) owns throwing, so the two can never fight.
    function pointerDown(e) {
      if (state !== 'aiming' || !held) return;
      if (e && e.cancelable) e.preventDefault();
      aimPointerId = (e && e.pointerId != null) ? e.pointerId : 'mouse';
      // Capture to the canvas so a drag keeps aiming even if the finger strays off it.
      try { if (e.target && e.target.setPointerCapture && e.pointerId != null) e.target.setPointerCapture(e.pointerId); } catch (_) {}
      aimFromEvent(e);
    }
    function pointerMove(e) {
      if (state !== 'aiming') return;
      var id = (e && e.pointerId != null) ? e.pointerId : 'mouse';
      if (aimPointerId !== null && id === aimPointerId) {     // dragging to aim
        if (e && e.cancelable) e.preventDefault();
        aimFromEvent(e); return;
      }
      if (aimPointerId === null && !isTouchEvt(e)) aimFromEvent(e);   // mouse hover keeps the reticle under the cursor
    }
    function pointerUp(e) {
      var id = (e && e.pointerId != null) ? e.pointerId : 'mouse';
      if (aimPointerId !== null && id === aimPointerId) aimPointerId = null;   // lifting never throws
    }

    // ---- Throw via the FLICK LANE (pointer) ----
    function laneDown(e) {
      if (state !== 'locked' || !held) return;         // only throwable once the aim is LOCKED
      if (e && e.cancelable) e.preventDefault();
      cancelCharge();                                  // a lane flick supersedes any keyboard bar
      try { lane.setPointerCapture(e.pointerId); } catch (_) {}
      flick = { id: e.pointerId, y0: e.clientY, power: 0 };
      lane.style.cursor = 'grabbing';
      setPower(0.001);
      if (laneHint) laneHint.textContent = 'Release to throw';
    }
    function laneMove(e) {
      if (!flick || e.pointerId !== flick.id) return;
      if (e && e.cancelable) e.preventDefault();
      var rect = renderer ? renderer.domElement.getBoundingClientRect() : { height: 480 };
      var refPx = Math.max(120, rect.height * geom.flickFullF);   // upward px for full power
      var up = flick.y0 - e.clientY;                              // upward positive
      flick.power = clamp(up / refPx, 0, 1);
      setPower(flick.power);
      liftHandle(up);
    }
    function laneUp(e) {
      if (!flick || e.pointerId !== flick.id) return;
      var g = flick; flick = null;
      try { lane.releasePointerCapture(e.pointerId); } catch (_) {}
      lane.style.cursor = 'grab';
      liftHandle(0);
      var up = g.y0 - e.clientY;
      if (up < 12) { setPower(0); if (laneHint) laneHint.textContent = laneHintText(); return; }   // a tap, not a throw — don't waste a dart
      doThrow(g.power);
    }
    function laneCancel(e) {
      if (!flick || e.pointerId !== flick.id) return;
      flick = null; liftHandle(0); setPower(0); lane.style.cursor = 'grab';
      if (laneHint) laneHint.textContent = laneHintText();
    }

    // ---- STAGE 1→2: LOCK the aim. Touch taps LOCK; desktop presses Space (which
    // also kicks off the power bar so the very next Space throws). Once locked, the
    // board no longer re-aims, so the throw can never drag your point of aim. ----
    function lockAim(fromKeyboard) {
      if (state !== 'aiming' || !held) return;
      setState('locked');
      if (fromKeyboard) startCharge();                 // desktop: locking also starts the power sweep
      else { setCoach(lockedCoach()); setPower(0); if (laneHint) laneHint.textContent = laneHintText(); }
    }
    // Back to STAGE 1 — discard the lock and aim again.
    function reaim() {
      if (state !== 'locked') return;
      cancelCharge(); flick = null;
      setState('aiming'); setCoach(aimCoach()); setPower(0); resetLane();
    }
    function keyReaim() { reaim(); }

    // ---- Throw via keyboard timing-bar (Space / Enter) — the desktop throw. After
    // LOCK, the bar sweeps 0↔1; the next press throws at whatever it reads (stop it
    // in the green). Touch players flick the lane instead. ----
    function startCharge() {
      if (state !== 'locked' || !held || flick) return;
      charge.on = true; charge.t = 0; charge.val = 0; charge.since = now();
      setCoach('Stop the bar in the green'); setPower(0.001);
    }
    function releaseCharge() {
      if (!charge.on) return;
      var held0 = now() - charge.since;
      charge.on = false;
      if (held0 < 0.1) { setPower(0); setCoach(lockedCoach()); return; }   // ignore a stray double-tap
      doThrow(charge.val);
    }
    function cancelCharge() { charge.on = false; charge.val = 0; }

    // ---- Launch: honest physics. The reticle is the intended pip; power shifts the
    // LANDING vertically (too hard = high, too soft = low) around a generous sweet
    // zone. We solve the launch velocity to hit that real landing point with the
    // standard arc, so the dart visibly flies there from the hand. ----
    function doThrow(power) {
      if ((state !== 'aiming' && state !== 'locked') || !held) return;   // 'aiming' = demo auto-throw; 'locked' = a real player
      lastPowerErr = power - geom.powerSweet;
      var e = clamp((power - geom.powerSweet) / Math.max(geom.powerBand, 0.001), -3, 3);   // signed error, in "bands"
      var offUp = e * (geom.powerKick / 3) * geom.Rb;          // too hard = high, too soft = low
      var offSide = e * (geom.sideKick / 3) * geom.Rb;         // 0 by default → aim alone owns left/right
      var landX = clamp(aim.x + offSide, -geom.aimLimit, geom.aimLimit);
      var landY = clamp(aim.y + offUp, -geom.aimLimit, geom.aimLimit);

      var P0 = handPose().pos;                          // launch from where the dart is held
      var Tw = toWorld(landX, landY, geom.faceProud);   // land on the visible face plane
      var tf = geom.flightT, G = new T.Vector3(0, -geom.grav, 0);
      // V0 = (T - P0)/tf - 0.5 G tf   → passes through T at tf under gravity G.
      var V0 = Tw.clone().sub(P0).multiplyScalar(1 / tf).addScaledVector(G, -0.5 * tf);

      active = { mesh: held, pv: P0.clone(), vv: V0, pp: P0.clone(), pd: P0.clone().sub(geom.faceCenter).dot(geom.N), t: 0, G: G };
      held = null; charge.on = false; flick = null;
      dartsLeft--;
      setState('throwing');
      setCoach(''); setPower(0); resetLane();
    }

    // ---- Flight ----
    function update(dt) {
      dt = Math.min(dt, 0.04);
      animateReticle(dt);
      if (hitMark && hitMark.material.opacity > 0) { hitMark.material.opacity = Math.max(0, hitMark.material.opacity - dt * 2.2); hitMark.scale.multiplyScalar(1 + dt * 3); }
      if (charge.on) {                                  // sweep the power bar 0→1→0
        charge.t += dt;
        var phase = (charge.t / geom.barPeriod) % 1;    // 0..1 of a half-cycle
        charge.val = phase < 0.5 ? phase * 2 : 2 - phase * 2;
        setPower(charge.val);
      }
      if ((state === 'aiming' || state === 'locked') && held) poseHeldDart(Math.sin(now() * 2.2) * geom.Rb * 0.012);
      // Demo AI: once the sight pause elapses, throw at a power near the sweet spot.
      if (demoMode && state === 'aiming' && held && demoAt && now() >= demoAt) {
        demoAt = 0;
        doThrow(clamp(geom.powerSweet + (Math.random() * 2 - 1) * geom.powerBand * 0.7, 0, 1));
      }
      if (state === 'throwing' && active) stepDart(dt);
      return state === 'throwing' || state === 'aiming';
    }

    function stepDart(dt) {
      var steps = Math.max(1, Math.ceil(dt / 0.005)), h = dt / steps;
      for (var i = 0; i < steps; i++) {
        active.vv.addScaledVector(active.G, h);
        active.pv.addScaledVector(active.vv, h);
        active.t += h;
        var d = active.pv.clone().sub(geom.faceCenter).dot(geom.N);
        if (active.pd > 0 && d <= 0) {                  // crossed the board face plane
          var f = active.pd / (active.pd - d);
          var cross = active.pp.clone().lerp(active.pv, f);
          return impact(cross, false);
        }
        active.pd = d; active.pp.copy(active.pv);
        if (active.pv.y < geom.floorY || active.t > 2.6) return impact(active.pv, true);
      }
      syncFlying();
    }
    function syncFlying() {
      if (!active) return;
      active.mesh.position.copy(active.pv);
      if (active.vv.lengthSq() > 1e-6) orientDart(active.mesh, active.vv);
    }

    function impact(worldPos, floored) {
      var b = toBoard(worldPos);
      var sc = floored ? { v: 0, label: 'MISS', ring: 'miss' } : scoreAt(b.x, b.y);
      // Stick the dart: tip at the impact, body trailing back along its travel.
      var dir = active.vv.clone().normalize();
      active.mesh.position.copy(worldPos).addScaledVector(dir, -geom.dartLen * 0.42);
      orientDart(active.mesh, dir);
      active.mesh.userData.stuck = true;
      active = null;
      // Hit marker + feedback.
      if (!floored && hitMark) { hitMark.position.set(b.x, b.y, geom.faceProud + geom.Rb * 0.03); hitMark.scale.set(1, 1, 1); hitMark.material.opacity = 0.9; hitMark.material.color.setHex(sc.v >= 50 ? 0xE63946 : sc.v >= 40 ? C.gold : C.greenHex); }
      var fb = throwFeedback();
      if (fb) showFeedback(fb.t, fb.c);
      resolveScore(sc, worldPos);
    }
    function throwFeedback() {
      var band = geom.powerBand;
      if (Math.abs(lastPowerErr) <= band * 0.6) return { t: 'Perfect!', c: '#22B84A' };
      if (lastPowerErr > band) return { t: 'Too hard', c: '#E63946' };
      if (lastPowerErr < -band) return { t: 'Too soft', c: '#9A7ADF' };
      return null;
    }
    function resolveScore(sc, worldPos) {
      score += sc.v; onScore(score);
      scorePop(sc.v, sc.label, worldPos);
      setState('between');
      var hold = sc.v >= 50 ? 1100 : 800;
      setTimeout(function () { if (state === 'between') nextDartOrEnd(); }, hold);
    }

    // ---- Keyboard / on-screen D-pad: aim nudges + throw ----
    function nudge(dir) {
      if (state !== 'aiming') return;
      var step = geom.Rb * 0.12;
      if (dir === 'left') setAim(aim.x - step, aim.y);
      else if (dir === 'right') setAim(aim.x + step, aim.y);
      else if (dir === 'up') setAim(aim.x, aim.y + step);
      else if (dir === 'down') setAim(aim.x, aim.y - step);
    }
    function keyAim(dir) { nudge(dir); }
    function setDir(dir) { nudge(dir); }
    // Space / Enter: 1st press LOCKS the aim (and starts the bar), 2nd press THROWS.
    function keyThrow() {
      if (!held) return;
      if (state === 'aiming') { lockAim(true); return; }
      if (state === 'locked') { if (charge.on) releaseCharge(); else startCharge(); }
    }
    function press(btn) { if (btn === 'throw') keyThrow(); }

    // ---- Visual polish ----
    var pulse = 0;
    function animateReticle(dt) {
      pulse += dt;
      if (!reticle) return;
      var locked = (state === 'locked');
      var s = 1 + Math.sin(pulse * 3) * (locked ? 0.04 : 0.08);          // steadier once locked
      reticle.scale.set(s, s, 1);
      reticle.visible = (state === 'aiming' || locked);
      // tint green when the aim is locked in, white while still aiming
      reticle.traverse(function (o) { if (o.material && o.material.color) o.material.color.setHex(locked ? 0x22B84A : 0xF7F8FA); });
    }

    function drawAttract(name) {
      setState('attract');
      if (held) { held.userData.stuck = false; poseHeldDart(0); }
      setCoach(''); setPower(0); resetLane();
      if (reticle) reticle.visible = false;
    }

    function detach() {
      clearDarts();
      disposeRig();
      [coach, powerEl, popHost, fbEl, lane, lockBtn, reaimBtn].forEach(function (el) { if (el && el.parentNode) el.parentNode.removeChild(el); });
      coach = powerEl = popHost = fbEl = lane = laneHandle = laneHint = lockBtn = reaimBtn = null;
      hideTuner();
    }
    function disposeRig() {
      if (!rig) return;
      rig.traverse(function (o) { if (o.geometry) o.geometry.dispose(); if (o.material) { (Array.isArray(o.material) ? o.material : [o.material]).forEach(function (m) { if (m.map) m.map.dispose(); m.dispose(); }); } });
      if (rig.parent) rig.parent.remove(rig);
      rig = null; faceMesh = null; reticle = null; hitMark = null;
    }

    // ============================================================
    //  DEV — DARTS DEBUG alignment panel (seat the board on the model)
    // ============================================================
    var panel = null;
    var TUNE_ROWS = [
      ['Nudge side', 'ox', 0.01, 3], ['Nudge up', 'oy', 0.01, 3], ['Nudge fwd', 'oz', 0.01, 3],
      ['Roll°', 'roll', 1, 0], ['Yaw°', 'yaw', 1, 0], ['Pitch°', 'pitch', 1, 0], ['Flip', 'flip', 1, 0],
      ['Radius', 'radiusF', 0.01, 2], ['Face proud', 'faceProud', 0.002, 3], ['Show face', 'showFace', 1, 0],
      ['Dart len', 'dartLenF', 0.02, 2], ['Hand dist', 'handDistF', 0.02, 2], ['Hand drop', 'handDownF', 0.02, 2], ['Hand side', 'handSideF', 0.02, 2],
      ['Gravity', 'grav', 0.2, 1], ['Flight t', 'flightT', 0.02, 2],
      ['Pwr sweet', 'powerSweet', 0.02, 2], ['Pwr band', 'powerBand', 0.01, 2], ['Pwr kick', 'powerKick', 0.02, 2], ['Side kick', 'sideKick', 0.02, 2],
      ['Flick full', 'flickFullF', 0.02, 2], ['Bar speed', 'barPeriod', 0.05, 2]
    ];
    function showTuner(on) {
      if (!dev || !mount) return;
      if (!on) { hideTuner(); return; }
      if (!panel) buildTuner();
      panel.style.display = 'block'; refreshTuner();
    }
    function hideTuner() { if (panel) panel.style.display = 'none'; }
    function buildTuner() {
      var BTN = 'cursor:pointer;width:22px;height:20px;border:0;border-radius:3px;background:#1A9E3E;color:#fff;font:inherit;line-height:1';
      var BTN2 = 'cursor:pointer;padding:4px 8px;border:0;border-radius:4px;background:#1A9E3E;color:#fff;font:inherit';
      panel = document.createElement('div');
      panel.style.cssText = 'position:absolute;top:10px;right:10px;z-index:31;display:none;width:236px;max-height:88%;overflow:auto;padding:10px 12px;background:rgba(8,5,19,.95);border:1px solid #D4A437;border-radius:6px;font:11px/1.45 ui-monospace,Menlo,Consolas,monospace;color:#f0e4c4';
      var html = '<b style="color:#D4A437">DARTS DEBUG</b><div style="margin:6px 0">';
      TUNE_ROWS.forEach(function (r) {
        html += '<div style="display:flex;align-items:center;gap:4px;margin:2px 0"><span style="flex:1">' + r[0] + '</span>' +
          '<button data-tprop="' + r[1] + '" data-tstep="' + (-r[2]) + '" style="' + BTN + '">&minus;</button>' +
          '<code data-tval="' + r[1] + '" style="width:50px;text-align:center"></code>' +
          '<button data-tprop="' + r[1] + '" data-tstep="' + r[2] + '" style="' + BTN + '">+</button></div>';
      });
      html += '</div><code data-tcode style="display:block;white-space:pre-wrap;word-break:break-all;background:#000;padding:6px;border-radius:4px;color:#D4A437;font-size:10px"></code>' +
        '<div style="display:flex;gap:6px;margin-top:6px"><button data-tcopy style="' + BTN2 + '">Copy JSON</button><button data-treset style="' + BTN2 + ';background:#5A5F69">Reset</button></div>';
      panel.innerHTML = html;
      mount.appendChild(panel);
      panel._code = panel.querySelector('[data-tcode]');
      panel.addEventListener('click', function (e) {
        var step = e.target.closest('button[data-tprop]');
        if (step) {
          var k = step.dataset.tprop, d = parseFloat(step.dataset.tstep);
          tune[k] = Math.round(((tune[k] || 0) + d) * 1000) / 1000;
          if (k === 'flip' || k === 'showFace') tune[k] = tune[k] > 0 ? 1 : 0;
          rebuild(); refreshTuner(); return;
        }
        if (e.target.closest('[data-tcopy]')) { if (navigator.clipboard) navigator.clipboard.writeText(panel._code.textContent); return; }
        if (e.target.closest('[data-treset]')) { tune = Object.assign({}, TUNE_DEFAULT); rebuild(); refreshTuner(); return; }
      });
    }
    function refreshTuner() {
      if (!panel) return;
      panel.querySelectorAll('[data-tval]').forEach(function (el) {
        var k = el.getAttribute('data-tval');
        var dec = (TUNE_ROWS.find(function (r) { return r[1] === k; }) || [, , , 2])[3];
        el.textContent = (tune[k] || 0).toFixed(dec);
      });
      panel._code.textContent = '{ ' + TUNE_ROWS.map(function (r) { return r[1] + ': ' + (tune[r[1]] || 0); }).join(', ') + ' }';
    }
    function rebuild() {
      var ks = state, ksc = score, kb = dartsLeft, ka = { x: aim.x, y: aim.y };
      clearDarts(); buildGeom(); buildMeshes(); newHeldDart();
      positionSweetZone();
      aim.x = ka.x; aim.y = ka.y; updateReticle();
      state = ks; score = ksc; dartsLeft = kb;
    }

    return {
      is3D: true,
      // No engine D-pad/buttons/swipe-hint — this module builds its own aim (board)
      // + LOCK + flick lane. touch:'darts' tells the engine to keep its hands off
      // (no gesture layer). showLives shows the darts-left pips. Keyboard routes
      // through the engine to keyAim / keyThrow / keyReaim.
      controls: { dpad: false, showLives: true, swipe: false, touch: 'darts', buttons: [] },
      tutorial: [
        { m: { g: 'board', t: 'Drag on the board to AIM the reticle' }, d: { k: ['MOUSE'], t: 'Move the mouse to AIM (arrows nudge)' } },
        { m: { g: 'lock', t: 'Tap LOCK to set your aim' }, d: { k: ['SPACE'], t: 'Press SPACE to LOCK &amp; start the power bar' } },
        { m: { g: 'flick', t: 'Flick up to THROW — flick farther for more power' }, d: { k: ['SPACE'], t: 'Press SPACE to THROW — stop the bar in the green' } }
      ],
      attach: attach, detach: detach,
      start: start, stop: stop, reset: reset, update: update, demo: demo,
      pointerDown: pointerDown, pointerMove: pointerMove, pointerUp: pointerUp,
      keyAim: keyAim, setDir: setDir, keyThrow: keyThrow, keyReaim: keyReaim, press: press,
      drawAttract: drawAttract, showTuner: showTuner,
      getScore: function () { return score; },
      getLives: function () { return dartsLeft; },
      getState: function () { return state; }
    };
  }
  create.is3D = true;   // tag the factory so the engine wires it into the scene

  window.DwArcadeGames = window.DwArcadeGames || {};
  window.DwArcadeGames.darts = create;
})();
